import React, { RefObject, useEffect, useLayoutEffect, useRef } from 'react';
import { Helmet, HelmetProvider } from '@dr.pogodin/react-helmet';
import { useIntl } from 'react-intl';
import FocusTrap from 'focus-trap-react';
import useOnClickOutside from 'use-onclickoutside';
import styles from './Modal.module.scss';
import { isIOSDevice } from '../../../utils';
import { ModalProps } from './types';
import messages from './messages';

const Modal = ({
  children,
  isVisible = false,
  onClose
}: ModalProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const { formatMessage } = useIntl();

  useOnClickOutside(contentRef as RefObject<HTMLElement>, onClose);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  useEffect(() => {
    const close = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [onClose]);

  useLayoutEffect(() => {
    // Do not remove me. For dynamic content, on mobile safari browser, modal opens up scrolled down all the way.
    setTimeout(() => contentRef.current?.scrollTo({ top: 0 }), 10);
  }, []);

  if (!isVisible) {
    return null;
  }

  return (
    <>
      {isIOSDevice() && (
        <HelmetProvider>
          <Helmet>
            <meta
              name="viewport"
              content="width=device-width, initial-scale=1.0, maximum-scale=1.0"
            />
          </Helmet>
        </HelmetProvider>
      )}
      <FocusTrap active={isVisible}>
        <div
          className={styles.modal}
          ref={contentRef}
          role="dialog"
          aria-modal="true"
          aria-label={formatMessage(messages.modalOpened)}
        >
          {children}
        </div>
      </FocusTrap>
      <div className={styles.background} aria-hidden="true" />
    </>
  );
};

export default Modal;
